import React, { Suspense, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import TitleBar from "../../components/navbar/TitleBar";
import SchoolBusNavMenu from "../../components/createForm/SchoolBusNavMenu";
import Footer from "../../components/common/Footer";
import SubTitle from "../../components/common/SubTitle";
import SearchFilter from "../../components/common/SearchFilter";
import SchoolDynamicTable from "../../components/createForm/SchoolDynamicTable";
import CreateVehicleForm from "../../components/createForm/CreateVehicleForm";
import activeEmoji from "../../images/vehicle/Active_icon.svg";
import inActiveEmoji from "../../images/vehicle/Inactive_icon.svg";
import deadEmoji from "../../images/vehicle/Dead_icon.svg";
import config from "../../config.js";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

const getPersonDisplayName = (person) => {
  if (!person) {
    return "";
  }

  const fullName = String(person.fullName || person.name || "").trim();
  const firstName = String(person.firstName || person.first_name || "").trim();
  const lastName = String(person.lastName || person.last_name || "").trim();

  return `${firstName} ${lastName}`.trim() || fullName;
};

const getPrimaryVehicleAttendant = (vehicle) => {
  if (!vehicle) {
    return null;
  }

  if (Array.isArray(vehicle.employees) && vehicle.employees.length > 0) {
    return vehicle.employees[0];
  }

  if (Array.isArray(vehicle.attendants) && vehicle.attendants.length > 0) {
    return vehicle.attendants[0];
  }

  return vehicle.employee || vehicle.attendant || null;
};

export default function SchoolBusVehicles() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [vehiclesData, setVehiclesData] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [routeOptions, setRouteOptions] = useState([]);

  const headers = [
    { key: "Name", value: "Name" },
    { key: "Reg. No.", value: "Vehicles Reg. No." },
    { key: "Routes", value: "Routes" },
    { key: "Drivers", value: "Drivers" },
    { key: "Attendants", value: "Attendants" },
    { key: "Status", value: "Status" },
    { key: "actions", value: "" },
  ];

  const filters = useMemo(
    () => [
      {
        id: "routes",
        label: "Routes",
        helperText: "Select route",
        options: routeOptions,
      },
      {
        id: "status",
        label: "Status",
        helperText: "Select vehicle status",
        options: [
          { value: "ACTIVE", label: "Active" },
          { value: "INACTIVE", label: "Inactive" },
          { value: "DEAD", label: "Maintenance" },
        ],
      },
    ],
    [routeOptions],
  );

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchImage = async (imageUrl) => {
    try {
      const response = await axios.get(imageUrl, {
        responseType: "arraybuffer",
        withCredentials: true,
      });
      const blob = new Blob([response.data], { type: "image/jpeg" });
      const imageDataUrl = URL.createObjectURL(blob);
      return imageDataUrl;
    } catch (error) {
      console.error(`Error fetching image from ${imageUrl}:`, error.message);
      return null;
    }
  };

  const getRouteId = (route) => route?.Id || route?.id || route?.ID;

  const extractUniqueRoutes = (vehicles) => {
    const uniqueRoutes = [];
    const routeIds = new Set();

    vehicles.forEach((vehicle) => {
      const routeId = getRouteId(vehicle.route);
      const routeName =
        vehicle.route?.name || vehicle.route?.Name || `Route ${routeId}`;

      if (routeId && !routeIds.has(routeId)) {
        routeIds.add(routeId);
        uniqueRoutes.push({
          value: routeId.toString(),
          label: routeName,
        });
      }
    });

    return uniqueRoutes;
  };

  const filterMostRecentDocuments = (docs) => {
    const groupedDocs = docs.reduce((acc, doc) => {
      if (!acc[doc.name]) {
        acc[doc.name] = [];
      }
      acc[doc.name].push(doc);
      return acc;
    }, {});

    const filteredDocs = Object.values(groupedDocs).map((group) => {
      return group.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      )[0];
    });

    return filteredDocs;
  };

  const transformVehicleData = async (vehicle) => {
    const allDocuments = vehicle.documents || [];
    const filteredDocuments = filterMostRecentDocuments(allDocuments);

    const profileImageDoc = filteredDocuments.find(
      (doc) => doc.name === "VehicleImage",
    );

    let profileImageUrl = null;
    if (profileImageDoc) {
      const imageUrlPath = `${config.baseURL}/upload/image?fileName=${
        profileImageDoc.location
      }&_=${Date.now()}`;

      try {
        profileImageUrl = await fetchImage(imageUrlPath);
      } catch (error) {
        console.error(
          `Failed to fetch image for vehicle ${vehicle.id}:`,
          error,
        );
        profileImageUrl = null;
      }
    }

    const attendant = getPrimaryVehicleAttendant(vehicle);

    return {
      Name: vehicle.name || "N/A",
      "Reg. No.": vehicle.VehicleNumber || "N/A",
      Routes: vehicle.route?.name || "N/A",
      Drivers: getPersonDisplayName(vehicle.driver) || "N/A",
      Attendants: getPersonDisplayName(attendant) || "N/A",
      Status: vehicle.status || "INACTIVE",
      fullData: {
        ...vehicle,
        documents: filteredDocuments,
        profileImage: profileImageUrl,
      },
    };
  };

  const fetchVehicles = async () => {
    setIsLoading(true);
    try {
      const { data: vehicleData } = await axios.get(
        `${config.baseURL}/vehicle/organization`,
        {
          withCredentials: true,
        },
      );

      if (!Array.isArray(vehicleData)) {
        setVehiclesData([]);
        return;
      }

      const sortedVehicleData = [...vehicleData].sort((a, b) => {
        const aTime = new Date(a.createdAt || a.updatedAt || 0).getTime();
        const bTime = new Date(b.createdAt || b.updatedAt || 0).getTime();

        if (aTime !== bTime) {
          return bTime - aTime;
        }

        return Number(b.id || 0) - Number(a.id || 0);
      });

      setRouteOptions(extractUniqueRoutes(sortedVehicleData));

      const transformedData = await Promise.all(
        sortedVehicleData.map(transformVehicleData),
      );

      setVehiclesData(transformedData);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      if (error.response?.status === 401) {
        navigate("/");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVehicleSubmit = async (formData) => {
    await fetchVehicles();
    setShowCreateForm(false);
    setExpandedRow(null);
  };

  const handleVehicleDelete = async () => {
    if (expandedRow === null) return;

    try {
      const userState = JSON.parse(localStorage.getItem("userState"));
      const response = await axios.delete(`${config.baseURL}/vehicle`, {
        data: {
          vehicleId: vehiclesData[expandedRow].fullData.id,
          organizationId: userState.user.organizationId,
        },
        withCredentials: true,
      });

      if (response?.status === 200) {
        await fetchVehicles();
        setExpandedRow(null);
      }
    } catch (error) {
      console.error("Error deleting vehicle:", error);
    }
  };

  const formatDataForTable = (data) =>
    data.map((vehicle) => ({
      ...vehicle,
      Status: (
        <div className="status-emoji">
          {vehicle.Status === "ACTIVE" && (
            <img src={activeEmoji} alt="Active" title="Active Vehicle" />
          )}
          {vehicle.Status === "INACTIVE" && (
            <img src={inActiveEmoji} alt="Inactive" title="Inactive Vehicle" />
          )}
          {(vehicle.Status === "DEAD" || vehicle.Status === "Maintenance") && (
            <img src={deadEmoji} alt="Maintenance" title="Maintenance" />
          )}
        </div>
      ),
    }));

  const filteredData = useMemo(() => {
    let filtered = [...vehiclesData];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((vehicle) =>
        Object.values(vehicle).some((value) =>
          String(value).toLowerCase().includes(query),
        ),
      );
    }

    if (filterValues.routes) {
      filtered = filtered.filter((vehicle) => {
        const routeId = getRouteId(vehicle.fullData.route);
        return routeId && String(routeId) === String(filterValues.routes);
      });
    }

    if (filterValues.status) {
      filtered = filtered.filter((vehicle) => {
        if (filterValues.status === "INACTIVE") {
          return ["INACTIVE", "DEAD", ""].includes(vehicle.Status);
        }
        return vehicle.Status === filterValues.status;
      });
    }

    return filtered;
  }, [searchQuery, filterValues, vehiclesData]);

  const renderExpandedRow = (row) => {
    const allDocuments = row.fullData.documents || [];
    const attendant = getPrimaryVehicleAttendant(row.fullData);

    const vehicleImage = allDocuments.find(
      (doc) => doc.name === "VehicleImage",
    );
    const otherDocuments = allDocuments.filter(
      (doc) => doc.name !== "VehicleImage",
    );

    const getImageUrl = (location) =>
      `${config.baseURL}/upload/image?fileName=${encodeURIComponent(location)}`;

    const transformedDocuments = otherDocuments.map((doc) => ({
      type: doc.name,
      image: getImageUrl(doc.location),
      file: null,
      id: doc.id,
      isExisting: true,
      isUploaded: true,
    }));

    const transformedData = {
      id: row.fullData.id,
      device_id: row.fullData.device_id || "",
      vehicleName: row.fullData.name || "",
      model: row.fullData.Model || "",
      registrationNumber: row.fullData.VehicleNumber || "",
      chassisNumber: row.fullData.chassisNumber || "",
      make: row.fullData.Make || "",
      yearOfManufacture: row.fullData.Year || "",
      route: getRouteId(row.fullData.route) || "",
      driver: row.fullData.driver?.id || row.fullData.driverId || "",
      attendant:
        attendant?.id ||
        row.fullData.employee?.id ||
        row.fullData.employeeId ||
        row.fullData.attendantId ||
        "",
      status: row.fullData.status || "",
      profileImage: row.fullData.profileImage,
      uploadedDocuments: transformedDocuments,
    };

    return (
      <CreateVehicleForm
        onClose={() => setExpandedRow(null)}
        onSubmit={handleVehicleSubmit}
        onDelete={handleVehicleDelete}
        initialData={transformedData}
        isEdit={true}
      />
    );
  };

  const handleRowClick = (rowIndex) => {
    setExpandedRow(expandedRow === rowIndex ? null : rowIndex);
    setShowCreateForm(false);
  };

  const handleCreateVehicle = () => {
    setShowCreateForm(true);
    setExpandedRow(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "100vh",
      }}
    >
      <Box>
        <TitleBar />
        <SchoolBusNavMenu />
        <SubTitle
          title={t("Vehicles")}
          add={t("+ Create Vehicles")}
          onAddClick={handleCreateVehicle}
        />
        <div className="table_container">
          {showCreateForm && (
            <CreateVehicleForm
              onClose={() => setShowCreateForm(false)}
              onSubmit={handleVehicleSubmit}
              isEdit={false}
            />
          )}

          <SearchFilter
            searchPlaceholder="search"
            filters={filters}
            onSearchChange={setSearchQuery}
            onFilterChange={setFilterValues}
            dataDownload={true}
            downloadRows={filteredData}
            downloadColumns={headers
              .filter((header) => header.key !== "actions")
              .map((header) => ({
                key: header.key,
                label: header.value,
              }))}
            downloadFileName="school-vehicles"
          />

          <Suspense fallback={<div className="loader"></div>}>
            <SchoolDynamicTable
              headers={headers}
              data={formatDataForTable(filteredData)}
              loading={isLoading}
              expandedRow={expandedRow}
              onRowClick={handleRowClick}
              renderExpandedRow={renderExpandedRow}
              profileImageColumn="Name"
            />
          </Suspense>
        </div>
      </Box>
      <Footer />
    </Box>
  );
}
